import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import stripeConstants from "../../Stripe/stripeConstants";
import {
  getIsAnySubsActiveByPlanOptions,
  hasUnlimitedResumeAccess,
} from "../../Stripe/function";
import { isAllowAllFeatureByDev } from "../../utils/devAccess";

const { STRIPE_ROLE_PLAN_OPTIONS_FOR_DOWNLOAD_PDF_DOCX } = stripeConstants;

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

// Hardcoded on purpose — keep in step with functions/aiQuota.js.
export const MAX_FREE_API_COUNT = 15;
export const MAX_PAID_API_COUNT = 500;

// Active $27 Stripe subscribers grandfathered onto careerclawnew.
// Server source of truth: Firestore grandfathered-subscribers/config.
// Keep this list in step so the AI badge shows 500/mo before subscriptions exist.
export const GRANDFATHER_PAID_EMAILS = [
  "awning-saber0c@icloud.com",
  "monikpatel15@gmail.com",
  "r.bhuva926@gmail.com",
].map(normalizeEmail);

const planOptions = STRIPE_ROLE_PLAN_OPTIONS_FOR_DOWNLOAD_PDF_DOCX;

const nowInSeconds = () => Math.floor(Date.now() / 1000);

const calendarMonthKey = (date = new Date()) =>
  `cal-${date.getUTCFullYear()}-${date.getUTCMonth() + 1}`;

/** Start of next UTC calendar month — when Razorpay paid AI counter rolls over. */
const nextCalendarMonthStartSec = (date = new Date()) =>
  Math.floor(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1) / 1000
  );

/**
 * Human-readable local date/time for when paid AI quota resets.
 * Returns null when periodEndSec is missing.
 */
export const formatQuotaResetAt = (periodEndSec) => {
  if (!Number.isFinite(periodEndSec) || periodEndSec <= 0) return null;
  try {
    return new Date(periodEndSec * 1000).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return null;
  }
};

/**
 * Pick the active paid subscription (if any) and a stable period key used to
 * reset the paid AI counter each billing cycle.
 */
export const getActivePaidPeriod = (
  allSubscriptions = [],
  options = planOptions
) => {
  if (isEmpty(allSubscriptions)) return null;

  const currentDateInSec = nowInSeconds();

  for (const item of allSubscriptions) {
    const data = item?.data || {};
    if (!options.includes(data.role)) continue;

    if (data.provider === "razorpay") {
      if (data.status !== "active") continue;
      return {
        subscriptionId: item.subscriptionId,
        provider: "razorpay",
        periodKey: calendarMonthKey(),
        periodEndSec: nextCalendarMonthStartSec(),
      };
    }

    const periodEndInSec = get(data, "current_period_end.seconds");
    if (typeof periodEndInSec !== "number" || currentDateInSec > periodEndInSec) {
      continue;
    }

    const periodStartInSec = get(data, "current_period_start.seconds");
    const periodKey =
      typeof periodStartInSec === "number"
        ? `stripe-${periodStartInSec}`
        : `stripe-end-${periodEndInSec}`;

    return {
      subscriptionId: item.subscriptionId,
      provider: "stripe",
      periodKey,
      periodStartSec: periodStartInSec ?? null,
      periodEndSec: periodEndInSec,
    };
  }

  return null;
};

/**
 * Resolve how many AI actions the user has used / may use right now.
 *
 * Firestore shape on api-counter/{uid} (written only by Cloud Functions):
 *   totalCount      — free-tier lifetime usage (never reset)
 *   paidPeriodKey   — billing-period key for the paid counter
 *   paidCount       — usage inside that paid period
 *
 * Nobody is unlimited except REACT_APP_IS_ALLOW_ALL_FEATURE_BY_DEV (local QA).
 * Comp-list emails still unlock downloads via hasUnlimitedResumeAccess, but
 * they are metered for AI like everyone else.
 */
export const resolveApiQuota = ({
  allSubscriptions = [],
  email = "",
  allAccessUsers = [],
  counterDoc = {},
} = {}) => {
  const isDevBypass = isAllowAllFeatureByDev();
  const isGrandfatherPaid = GRANDFATHER_PAID_EMAILS.includes(
    normalizeEmail(email)
  );
  const isPaid =
    isGrandfatherPaid ||
    getIsAnySubsActiveByPlanOptions(allSubscriptions, planOptions);
  const hasDownloadAccess = hasUnlimitedResumeAccess({
    allSubscriptions,
    planOptions,
    email,
    allAccessUsers,
  });

  const freeUsed = Number(counterDoc.totalCount) || 0;
  const storedPeriodKey = counterDoc.paidPeriodKey || null;
  const storedPaidCount = Number(counterDoc.paidCount) || 0;

  // Local / QA only — never ship production builds with this flag true.
  if (isDevBypass) {
    return {
      tier: "comp",
      isPaid: true,
      hasDownloadAccess: true,
      isUnlimited: true,
      used: 0,
      max: null,
      remaining: null,
      isExhausted: false,
      isPaidLimitReached: false,
      periodKey: null,
      periodEndSec: null,
      resetsAtLabel: null,
      freeUsed,
      paidCount: storedPaidCount,
    };
  }

  if (isPaid) {
    const period = getActivePaidPeriod(allSubscriptions, planOptions);
    const periodKey = period?.periodKey || calendarMonthKey();
    const periodEndSec =
      typeof period?.periodEndSec === "number"
        ? period.periodEndSec
        : nextCalendarMonthStartSec();
    const paidCount =
      storedPeriodKey && storedPeriodKey === periodKey ? storedPaidCount : 0;
    const max = MAX_PAID_API_COUNT;
    const remaining = Math.max(max - paidCount, 0);
    const isExhausted = paidCount >= max;

    return {
      tier: "paid",
      isPaid: true,
      hasDownloadAccess,
      isUnlimited: false,
      used: paidCount,
      max,
      remaining,
      isExhausted,
      isPaidLimitReached: isExhausted,
      periodKey,
      periodEndSec,
      resetsAtLabel: formatQuotaResetAt(periodEndSec),
      freeUsed,
      paidCount,
    };
  }

  const max = MAX_FREE_API_COUNT;
  const remaining = Math.max(max - freeUsed, 0);
  const isExhausted = freeUsed >= max;

  return {
    tier: "free",
    isPaid: false,
    // Comp-list may still unlock PDF/DOCX downloads without free AI.
    hasDownloadAccess,
    isUnlimited: false,
    used: freeUsed,
    max,
    remaining,
    isExhausted,
    isPaidLimitReached: false,
    periodKey: null,
    periodEndSec: null,
    resetsAtLabel: null,
    freeUsed,
    paidCount: storedPaidCount,
  };
};

/**
 * Local badge update after a successful AI response. Server already wrote
 * api-counter; this only keeps the UI in sync until the next fetch.
 */
export const nextCounterAfterSpend = (counterDoc = {}, quota) => {
  if (quota.tier === "comp" || quota.isUnlimited) {
    return {
      totalCount: Number(counterDoc.totalCount) || 0,
      paidPeriodKey: counterDoc.paidPeriodKey || null,
      paidCount: Number(counterDoc.paidCount) || 0,
    };
  }

  if (quota.tier === "paid") {
    const periodKey = quota.periodKey;
    const previousKey = counterDoc.paidPeriodKey || null;
    const base =
      previousKey && previousKey === periodKey
        ? Number(counterDoc.paidCount) || 0
        : 0;

    return {
      totalCount: Number(counterDoc.totalCount) || 0,
      paidPeriodKey: periodKey,
      paidCount: base + 1,
    };
  }

  return {
    totalCount: (Number(counterDoc.totalCount) || 0) + 1,
    paidPeriodKey: counterDoc.paidPeriodKey || null,
    paidCount: Number(counterDoc.paidCount) || 0,
  };
};

export default {
  MAX_FREE_API_COUNT,
  MAX_PAID_API_COUNT,
  getActivePaidPeriod,
  formatQuotaResetAt,
  resolveApiQuota,
  nextCounterAfterSpend,
};
